"use strict";

import React from "react";
import { RawScore, WrappedText } from "./components.js";
import { simpleTitleCase } from "./shared.js";
import { PUZZLE_CONFIG } from "./puzzle.js";

const BoxDefs = () => (
  <g className="box-defs">
    <path
      id="back_path"
      d="M390,464 L390,672 L0,672 L0,464 L0,77.1526677 C50.9792162,29.3056027 119.567263,0 195,0 C270.432737,0 339.020784,29.3056027 390,77.1526677 L390,464 Z"
    />
    <path
      id="front_path"
      d="M31.6669702,1.0658141e-14 C78.8345063,34.5796445 137.032538,55 200,55 C262.967462,55 321.165494,34.5796445 368.33303,1.0658141e-14 L396,0 C398.209139,-4.05812251e-16 400,1.790861 400,4 L400,246 C400,248.209139 398.209139,250 396,250 L4,250 C1.790861,250 2.705415e-16,248.209139 0,246 L0,4 C-2.705415e-16,1.790861 1.790861,4.05812251e-16 4,0 L31.6669702,0 Z"
    />
    <filter
      id="thickness_fx"
      x="-0.4%"
      y="-0.4%"
      width="100.8%"
      height="100.8%"
      filterUnits="objectBoundingBox"
    >
      <feOffset dx="0" dy="3" in="SourceAlpha" result="shadowOffsetInner1" />
      <feComposite
        in="shadowOffsetInner1"
        in2="SourceAlpha"
        operator="arithmetic"
        k2="-1"
        k3="1"
        result="shadowInnerInner1"
      />
      <feColorMatrix
        values="0 0 0 0 1   0 0 0 0 1   0 0 0 0 1  0 0 0 0.169143357 0"
        type="matrix"
        in="shadowInnerInner1"
      />
    </filter>
  </g>
);

const CrayonDefs = () => (
  <g className="crayon-defs">
    <path
      id="crayon_path"
      d="M52,47 L55,47 C57.7614237,47 60,49.2385763 60,52 L60,322 C60,324.761424 57.7614237,327 55,327 L5,327 C2.23857625,327 -3.2145368e-15,324.761424 -3.55271368e-15,322 L-3.55271368e-15,52 C-3.89089055e-15,49.2385763 2.23857625,47 5,47 L8,47 L24.5226516,1.31972784 C24.8091367,0.527680915 25.5611381,-8.7108808e-14 26.4034041,-8.72635297e-14 L33.5965959,-8.52651283e-14 C34.4388619,-8.541985e-14 35.1908633,0.527680915 35.4773484,1.31972784 L52,47 Z"
    />
    <path
      id="wrapper_path"
      transform="translate(30.000000, 187.000000) scale(1, -1) translate(-30.000000, -187.000000)"
      d="M0,63 L60,63 L60,291 L0,291 L0,63 Z M0,301 L60,301 L60,311 L0,311 L0,301 Z"
    />
    <linearGradient
      id="cylinder_fill"
      x1="0%"
      y1="50%"
      x2="91.9196886%"
      y2="50%"
    >
      <stop stopColor="#690C0C" stopOpacity="0.203832654" offset="0%" />
      <stop
        stopColor="#B59797"
        stopOpacity="0.597854393"
        offset="27.8247144%"
      />
      <stop stopColor="#C1AEAE" stopOpacity="0.6" offset="53.1949964%" />
      <stop stopColor="#690C0C" stopOpacity="0.399060236" offset="100%" />
    </linearGradient>
    <linearGradient id="shadow_fill" x1="50%" y1="0%" x2="50%" y2="100%">
      <stop stopColor="#000000" stopOpacity="0" offset="0%" />
      <stop stopColor="#040404" stopOpacity="0" offset="11.2930526%" />
      <stop stopColor="#2D2D2D" stopOpacity="0.1" offset="14.3419692%" />
      <stop stopColor="#0A0A0A" stopOpacity="0" offset="14.4273658%" />
      <stop stopColor="#000000" stopOpacity="0.1" offset="100%" />
    </linearGradient>
    <linearGradient id="reflection_fill" x1="100%" y1="50%" x2="0%" y2="50%">
      <stop stopColor="#FFFFFF" stopOpacity="0" offset="0%" />
      <stop stopColor="#FFFFFF" offset="48.4031883%" />
      <stop stopColor="#FFFFFF" stopOpacity="0" offset="100%" />
    </linearGradient>
  </g>
);

const CRAYON_OPTS = {
  // via `grep '<use fill="#.*" xlink:href="#path-\d"></use>' -R crayons_assets/crayons`
  WAX_COLORS: {
    // verystrong,  strong,    neutral,   weak,     veryweak
    R: ["#E82544", "#FB3C5A", "#FF8296", "#FEA2B1", "#FFCBD3"],
    O: ["#FF6A00", "#FF8935", "#FFA668", "#FFB988", "#FFCDAA"],
    Y: ["#FFD438", "#FFE038", "#FFEE59", "#FFEF5D", "#FFF495"],
    G: ["#51B664", "#77DA8A", "#8BE49C", "#A3F5B2", "#C2FBCD"],
    B: ["#0467D5", "#2488F7", "#489FFF", "#7FBCFF", "#BFDDFF"],
    V: ["#6F35D8", "#925EF0", "#AB7DFF", "#CAACFF", "#E0CEFF"],
  },

  // via `grep -o '<use fill-opacity="[^" ]*" fill="url(#linearGradient-1)"' -R crayons_assets/crayons`
  CYL_OPACITY: {
    //  vs,   s,   n,   w,   vw
    R: [0.2, 0.1, 0.1, 0.1, 0.1],
    O: [0.2, 0.1, 0.1, 0.1, 0.1],
    Y: [0.5, 0.5, 0.4, 0.4, 0.3],
    G: [0.4, 0.25, 0.25, 0.25, 0.25],
    B: [0.5, 0.5, 0.5, 0.4, 0.3],
    V: [0.2, 0.25, 0.5, 0.4, 0.3],
  },

  // shadows same, via `grep -o '<use fill="url(#linearGradient-2)" xlink:href="#path-\d">' -R . | wc -l`

  // via `grep 'fill="url(#linearGradient-3)"' -R crayons_assets/crayons`
  HAS_REFLECTION: {
    // vs, s, n, w, vw
    R: [1, 1, 1, 1, 1],
    O: [1, 1, 1, 1, 1],
    Y: [0, 0, 0, 0, 0],
    G: [1, 1, 1, 1, 1],
    B: [1, 1, 1, 1, 1],
    V: [1, 1, 1, 1, 0],
  },

  // via `grep -o 'fill="#000000" opacity="[^" ]*"' -R crayons_assets/crayons`
  WRAP_OPACITY: {
    //  vs,   s,   n,   w,    vw
    R: [0.4, 0.5, 0.2, 0.15, 0.11],
    O: [0.3, 0.25, 0.2, 0.15, 0.11],
    Y: [0.3, 0.25, 0.2, 0.15, 0.11],
    G: [0.15, 0.25, 0.2, 0.15, 0.11],
    B: [0.3, 0.25, 0.2, 0.15, 0.11],
    V: [0.3, 0.25, 0.2, 0.15, 0.11],
  },
};

const _NA_SETTINGS = {
  WAX_COLORS: "#E7E7E7",
  CYL_OPACITY: 0.2,
  HAS_REFLECTION: 1,
  WRAP_OPACITY: 0,
};

// copy _NA_SETTINGS into all the per-color CRAYON_OPTS arrays
Object.keys(CRAYON_OPTS).forEach((ctxKey) => {
  Object.keys(CRAYON_OPTS[ctxKey]).forEach((colKey) => {
    let arr = CRAYON_OPTS[ctxKey][colKey],
      naVal = _NA_SETTINGS[ctxKey];
    arr.push(naVal, naVal); // NOTE: we push for both '0' and 'X' value!
  });
});

const RawCrayon = ({
  colorKey: c,
  rankIndex: r,
  transform = null,
  theming: t = CRAYON_OPTS,
}) => (
  <g className="crayon" transform={transform}>
    <use className="wax" fill={t.WAX_COLORS[c][r]} xlinkHref="#crayon_path" />
    <use
      fillOpacity={t.CYL_OPACITY[c][r]}
      fill="url(#cylinder_fill)"
      style={{ mixBlendMode: "multiply" }}
      xlinkHref="#crayon_path"
    />
    <use fill="url(#shadow_fill)" xlinkHref="#crayon_path" />
    {t.HAS_REFLECTION[c][r] ? (
      <use
        fillOpacity="0.2"
        fill="url(#reflection_fill)"
        xlinkHref="#crayon_path"
      />
    ) : null}
    <use
      fill="#000000"
      opacity={t.WRAP_OPACITY[c][r]}
      style={{ mixBlendMode: "overlay" }}
      xlinkHref="#wrapper_path"
    />
  </g>
);

export function PointerIcon({ size = 45 }) {
  return (
    <svg
      className="touch-prompt-icon"
      width={size}
      height={size}
      viewBox="0 0 90 90"
    >
      <RawPointerIcon x={45} y={45} />
    </svg>
  );
}

const RawPointerIcon = ({ x = 35, y = 435, size = 45, style = {} }) => (
  <g
    className="pointer-icon"
    transform={`translate(${x} ${y}) scale(${(size / 36).toFixed(
      3,
    )}) translate(-36 -36)`}
    style={style}
  >
    <circle cx="36" cy="36" r="36" style={{ opacity: 0.5, fill: "#E9E9E9" }} />
    <circle cx="35.8" cy="35.8" r="28.8" style={{ fill: "#FFFFFF" }} />

    <path
      d="M46,48.2c0-0.1,0-0.2,0-0.3c0-1.4,0-2.7,0-4.1c0-0.1,0-0.1,0-0.2c0.5,0,1,0,1.5,0c0-0.5,0-0.9,0-1.3c0-3,0-6,0-9
    c0-0.1,0-0.2,0-0.3c0,0,0,0,0,0c0.5,0,1,0,1.5,0c0,0.1,0,0.2,0,0.3c0,1.6,0,3.2,0,4.8c0,0.2,0,0.3,0,0.5c0,0.2,0,0.4,0,0.6
    c0,0.3,0,0.7,0,1c0,1,0,2.1,0,3.1c0,0.1,0,0.2,0,0.4c-0.5,0-1,0-1.5,0.1c0,1.5,0,3,0,4.5C47,48.2,46.5,48.2,46,48.2L46,48.2z"
    />
    <path
      d="M46,48.2c0,0.1,0,0.1,0,0.2c0,1.4,0,2.9,0,4.4c-5.1,0-10.2,0-15.3,0c0-1.5,0-3,0-4.6c-0.5,0-1,0-1.5,0c0-1,0-2,0-3.1
    c0.5,0,1,0,1.5,0c0,1,0,2,0,3c0.5,0,1,0,1.5,0c0,1,0,2,0,3c4.1,0,8.1,0,12.2,0c0-1,0-2,0-3C45,48.2,45.5,48.2,46,48.2
    C46,48.2,46,48.2,46,48.2z"
    />
    <path
      d="M27.6,34.6c0.4,0,0.8,0,1.2-0.1c0.1,0,0.2,0,0.4,0c0-4.6,0-9.1,0-13.7c0.5,0,1,0,1.5,0c0,5.6,0,11.1,0,16.7
    c-0.5,0-1,0-1.5,0c0-0.5,0-1,0-1.5c-0.5,0-1,0-1.6,0C27.6,35.5,27.6,35,27.6,34.6L27.6,34.6z"
    />
    <path
      d="M38.4,28.4c-0.2,0-0.3,0-0.5,0c-0.8,0-1.5,0-2.3,0c-0.1,0-0.1,0-0.3,0c0,2,0,4,0,6.1c-0.5,0-1,0-1.5,0c0-4.6,0-9.1,0-13.7
    c0.5,0,1,0,1.5,0c0,2,0,4,0,6c1,0,2,0,3,0C38.3,27.4,38.3,27.9,38.4,28.4C38.4,28.4,38.4,28.4,38.4,28.4z"
    />
    <path
      d="M42.9,30c-1,0-2,0-3.1,0c0,1.5,0,3,0,4.6c-0.5,0-1,0-1.5,0c0-0.7,0-1.4,0-2.1c0-1.3,0-2.6,0-3.8c0,0,0-0.1,0-0.1
    c0,0,0,0,0,0c0.7,0,1.4,0,2,0c0.8,0,1.7,0,2.5,0C42.9,29,42.9,29.5,42.9,30L42.9,30z"
    />
    <path
      d="M42.9,30c1,0,2,0,3,0c0,0.5,0,1,0,1.5c0.5,0,1,0,1.6,0c0,0.5,0,1,0,1.5c0,0,0,0,0,0c-0.5,0-1,0-1.6,0c0-0.3,0-0.7,0-1
    c0-0.5,0-0.5-0.5-0.5c-0.3,0-0.7,0-1.1,0c0,1.5,0,3.1,0,4.6c-0.5,0-1,0-1.5,0c0-0.1,0-0.1,0-0.1c0-1.9,0-3.9,0-5.8
    C42.9,30,42.9,30,42.9,30C42.9,30,42.9,30,42.9,30z"
    />
    <path
      d="M24.6,39.1c0-0.5,0-0.9,0-1.3c0-0.2,0.1-0.2,0.2-0.2c0.4,0,0.8,0,1.3,0c0,0.5,0,1,0,1.5c0.5,0,1,0,1.5,0c0,1,0,2,0,3
    c0.5,0,1,0,1.5,0c0,1,0,2,0,3.1c-0.5,0-1,0-1.5,0c0-1,0-2,0-3c-0.4,0-0.7,0-1,0c-0.5,0-0.5,0-0.5-0.5c0-0.7,0-1.4,0-2.2
    c0-0.2-0.1-0.4-0.3-0.3C25.4,39.2,25,39.1,24.6,39.1z"
    />
    <path
      d="M27.6,34.6c0,0-0.1,0-0.1,0c-0.9,0-1.8,0-2.7,0c0,0-0.1,0-0.1,0c0,1,0,2,0,3c-0.5,0-1,0-1.5,0c0-1.5,0-3,0-4.5
    c1.5,0,3,0,4.5,0C27.6,33.5,27.6,34.1,27.6,34.6C27.6,34.6,27.6,34.6,27.6,34.6z"
    />
    <path d="M33.7,20.8c-1,0-2,0-3,0c0-0.5,0-1,0-1.5c1,0,2,0,3,0C33.7,19.8,33.7,20.3,33.7,20.8z" />
  </g>
);

const EssentialPopup = ({ essential, result, colorKey: c }) => {
  const [W, H] = [200, 135];
  const AS = 20; // "anchor size"
  const E = 1; // "error" (anchor bleed fudge)

  const RY = -60; // "result [label] y"
  const RH = 30; // "result [label] height"
  const RW = 140; // "result [label] width"
  const NY = -110; // "name [label] y"

  return (
    <g className="essential-popup">
      <g className="panel" fill={SCRIBBLE_COLORS[c]}>
        <polygon
          className="anchor"
          points={`0,0 -${AS},-${AS + E} ${AS},-${AS + E} 0,0`}
        />
        <rect className="main" x={-W / 2} width={W} y={-(AS + H)} height={H} />
      </g>
      <WrappedText
        className="name-label"
        x={0}
        y={NY}
        config={{ ALIGN: "center" }}
        textAnchor="middle"
      >
        {essential.name}
      </WrappedText>
      {result && result.rank ? (
        <g className="result">
          <rect
            className="background"
            x={-RW / 2}
            width={RW}
            y={RY}
            height={RH}
          />
          <text
            className="score-label"
            x={0}
            y={RY + RH / 2}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {result.rank.label}
          </text>
        </g>
      ) : null}
    </g>
  );
};

// TOOD: combine with `EssentialPopup` and toggle based on `result`?
const EssentialNamePopup = ({ essential, colorKey: c }) => {
  const [W, H] = [200, 100];
  const AS = 20; // "anchor size"
  const E = 1; // "error" (anchor bleed fudge)
  const NY = 70; // "name [label] y"

  return (
    <g className="essential-popup">
      <g className="panel" fill={SCRIBBLE_COLORS[c]}>
        <polygon
          className="anchor"
          points={`0,0 -${AS},${AS + E} ${AS},${AS + E} 0,0`}
        />
        <rect className="main" x={-W / 2} width={W} y={AS} height={H} />
      </g>
      <WrappedText
        className="name-label"
        x={0}
        y={NY}
        config={{ ALIGN: "center" }}
        textAnchor="middle"
      >
        {essential.name}
      </WrappedText>
    </g>
  );
};

// codes are X012345
const resultRank = (result) => (result.code === "X" ? 6 : 5 - +result.code);

export function CrayonBox({
  essentials,
  survey: { themeSlugs },
  size = 250,
  labels = true,
  popups = false,
  allCrayons = false,
  _animStyles = {},
  className: cn = "",
}) {
  const COLUMN_KEYS = [..."ROYGBV"];
  const COLOR_RANKS = "verystrong,strong,neutral,weak,veryweak".split(",");

  return (
    <svg
      className={`overall-rep crayonbox ${cn}`}
      width={((size * 405) / 672).toFixed(1)}
      height={size}
      viewBox="-5 0 400 672"
      style={_animStyles.wholeBox}
    >
      <defs>
        <BoxDefs />
        <CrayonDefs />
      </defs>

      <g className="back">
        <g style={_animStyles.boxBack}>
          <use fill="#ECECEC" xlinkHref="#back_path"></use>
          <use filter="url(#thickness_fx)" xlinkHref="#back_path" />
        </g>
      </g>

      {_animStyles._safariWorkaround ? (
        <g className="safari-workaround">
          <rect
            x={0}
            y={-1000}
            width={1}
            height={2000}
            fill="none"
            stroke="none"
          />
        </g>
      ) : null}

      <g className="crayons" transform="translate(0 25)">
        {COLUMN_KEYS.map((colKey, colIdx) => {
          let slug = themeSlugs[colKey],
            essential = essentials[slug],
            resRnkIdx = resultRank(essential._results.latest),
            minRnkIdx = allCrayons ? 0 : resRnkIdx; // NOTE: 0 is strongest
          return (
            <g
              key={colKey}
              data-slug={essential.slug}
              className="column"
              transform={`translate(${66 * colIdx} 0)`}
            >
              <g style={_animStyles.column && _animStyles.column[colIdx]}>
                {COLOR_RANKS.map((rnkKey, rnkIdx) => {
                  if (rnkIdx < minRnkIdx) return null;

                  let animFn =
                    rnkIdx < resRnkIdx
                      ? _animStyles.missingCrayon
                      : _animStyles.presentCrayon;
                  return (
                    <g key={rnkKey} style={animFn && animFn(colIdx, rnkIdx)}>
                      <RawCrayon
                        colorKey={colKey}
                        rankIndex={rnkIdx}
                        transform={`translate(0 ${80 * rnkIdx})`}
                      />
                    </g>
                  );
                })}
              </g>
            </g>
          );
        })}
      </g>

      <g className="front" transform="translate(-5 422)">
        <g style={_animStyles.boxFront}>
          <use fill="#818181" xlinkHref="#front_path"></use>
          <use filter="url(#thickness_fx)" xlinkHref="#front_path" />

          {labels ? (
            <g className="box-label" transform="translate(200, 113)">
              <g style={_animStyles.frontLabel}>
                <text className="static">Overall</text>
                <WrappedText className="status">
                  {simpleTitleCase(
                    essentials.overall._results.latest.rank.overall_long_label,
                  )}
                </WrappedText>
              </g>
            </g>
          ) : null}
        </g>
      </g>

      {popups ? (
        <g className="popups" transform="translate(0 25)">
          {COLUMN_KEYS.map((colKey, colIdx) => {
            let slug = themeSlugs[colKey],
              essential = essentials[slug],
              resRnkIdx = resultRank(essential._results.latest),
              minRnkIdx = allCrayons ? 0 : resRnkIdx; // NOTE: 0 is strongest
            return (
              <a
                key={colKey}
                className="column"
                data-slug={essential.slug}
                transform={`translate(${66 * colIdx} 0)`}
                xlinkHref="#"
                onClick={(evt) => {
                  evt.preventDefault();

                  // WORKAROUND: Safari does not blur us when tapping away, so allow a second tap to clear
                  // ALSO: Safari seems to reset the `document.activeElement` while processing the *next* tap
                  //       so we track it ourselves via .__inq_focus
                  if (
                    evt.currentTarget === document.activeElement ||
                    evt.currentTarget.__inq_focus
                  ) {
                    document.documentElement.focus();
                    // TODO: this is fine, but also needs to happen when focus is lost via other mechanisms…
                    delete evt.currentTarget.__inq_focus;
                  } else {
                    evt.currentTarget.focus();
                    evt.currentTarget.__inq_focus = true;
                  }
                }}
              >
                <rect
                  x={0}
                  y={0}
                  width={60}
                  height={645}
                  fill="none"
                  pointerEvents="all"
                />
                <g style={_animStyles.column && _animStyles.column[colIdx]}>
                  {allCrayons ? (
                    <g transform={`translate(30 ${80 * 5.5 - 2})`}>
                      <EssentialNamePopup
                        essential={essential}
                        colorKey={colKey}
                      />
                    </g>
                  ) : (
                    <g transform={`translate(30 ${80 * minRnkIdx - 2})`}>
                      <EssentialPopup
                        essential={essential}
                        result={essential._results.latest}
                        colorKey={colKey}
                      />
                    </g>
                  )}
                </g>
              </a>
            );
          })}
          {"hoverHint" in _animStyles ? (
            <RawPointerIcon style={_animStyles.hoverHint} />
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}

export function Crayon({
  essential,
  fullStrength = false,
  survey: { themeSlugs },
  size = 10,
  rotate = 0,
}) {
  let colKey = themeSlugs[essential.slug],
    rnkIdx = fullStrength ? 0 : resultRank(essential._results.latest);
  return (
    <svg
      className="individual-rep single-crayon"
      width={((size * 60) / 330).toFixed(1)}
      height={size}
      viewBox="0 -1 60 329"
    >
      <defs>
        <CrayonDefs />
      </defs>
      <RawCrayon
        colorKey={colKey}
        rankIndex={rnkIdx}
        transform={
          rotate
            ? `translate(30 163.5) rotate(${rotate}) translate(-30 -163.5)`
            : null
        }
      />
    </svg>
  );
}

import { SCRIBBLE_PATH } from "./crayon_data.js";

// NOTE: these are no longer used for the scribbles themselves!
// via `grep -o 'fill="#[^" ]*"' -R scribble`
export const SCRIBBLE_COLORS = {
  R: "#E92645",
  O: "#FF6A00",
  Y: "#F1AF00",
  G: "#42B558",
  B: "#0B78E8",
  V: "#6F35D8",
};

export function PositionedScribble({
  item: essential,
  result,
  survey: { themeSlugs },
  size = PUZZLE_CONFIG.sideLen,
  context = "centered",
  fullStrength = false,
}) {
  let ctr = size / 2,
    colKey = themeSlugs[essential.slug],
    rnkIdx = fullStrength ? 0 : resultRank(essential._results.latest);
  return (
    <g
      fill={CRAYON_OPTS.WAX_COLORS[colKey][rnkIdx]}
      // FUDGE: this transform makes the scribble line up nicely in graph legend
      transform={context === "centered" ? "translate(16, 10)" : null}
    >
      <path d={SCRIBBLE_PATH} />
      {context === "label" ? (
        <g className="label">
          <circle cx={ctr} cy={ctr} r={size / 3} />
          <text
            x={ctr}
            y={ctr}
            textAnchor="middle"
            //dominantBaseline="central"
            dy="0.35em"
          >
            <RawScore result={result} Wrapper="tspan" />
          </text>
        </g>
      ) : null}
    </g>
  );
}

export function CrayonScribble({
  item: essential,
  survey,
  size = 10,
  score_label = false,
}) {
  return (
    <svg
      className="individual-rep crayon-scribble"
      width={size}
      height={size}
      viewBox="0 0 28 28"
    >
      <PositionedScribble
        survey={survey}
        item={essential}
        result={essential._results.latest}
        size={size}
        label={score_label}
        context={score_label ? "label" : "standalone"}
      />
    </svg>
  );
}
